import React, { useRef } from "react";

import useRafScroll from "./useRafScroll";

// How far through the document the reader is.
//
// Rendered once by the router's layout route, next to <BackToTop>, for the
// reason that component's own note gives: a control that appeared on one page
// and not the others would read as a quirk of that page rather than as something
// the site does. Anchoring it under the service pages' sticky jump nav would
// have done exactly that, since the nav only exists on pages with three or more
// labelled sections.
//
// Not a role="progressbar". It reports the same thing the scrollbar reports, in
// the same way, and having a screen reader announce "twelve percent" as the
// reader moves would be noise rather than information.
//
// It also keeps working under prefers-reduced-motion — hence the opt-out below.
// This is a readout of position, not an animation, in the same category as the
// scrollbar itself; pausing it would pin the bar at zero for the whole page,
// which is a worse answer than not drawing one at all. Only its smoothing is
// dropped, in _motion.scss.
function ScrollProgress() {
  const barRef = useRef(null);

  useRafScroll(
    (y) => {
      const bar = barRef.current;
      if (!bar) return;

      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      // A page shorter than the window has nothing to read through, and the
      // division would be by zero — which paints an Infinity-wide bar across the
      // top of every short page rather than no bar at all.
      const progress =
        scrollable > 0 ? Math.min(1, Math.max(0, y / scrollable)) : 0;

      bar.style.setProperty("--scroll-progress", progress.toFixed(4));
    },
    { pauseForReducedMotion: false }
  );

  return (
    <div className="scroll-progress" aria-hidden="true">
      <div className="scroll-progress-bar" ref={barRef} />
    </div>
  );
}

export default ScrollProgress;
